import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo } from "react";
import { AddThirdPartyBillingAccountData } from "./third-party-billing";
import {
  CreateThirdPartyBillingForm,
  ThirdPartyBillingInput,
  thirdPartyBillingDefault,
} from "./third-party-billing-form";

interface AddThirdPartyBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddAccount: (
    account: AddThirdPartyBillingAccountData,
    options?: { hidden?: boolean },
  ) => Promise<{ success: boolean }>;
  showUseOnceOption?: boolean;
}

export const AddThirdPartyBillingModal = memo(
  function AddThirdPartyBillingModal({
    isOpen,
    onClose,
    onAddAccount,
    showUseOnceOption,
  }: AddThirdPartyBillingModalProps) {
    const form = useInput(ThirdPartyBillingInput, thirdPartyBillingDefault());

    const getAccountData = (): AddThirdPartyBillingAccountData => ({
      accountName: form.inputs.accountName.value,
      carrier: form.inputs.carrier.value,
      accountNumber: form.inputs.accountNumber.value,
      country: form.inputs.country.value,
      postalCode: form.inputs.postalCode.value,
    });

    const handleSubmit = useHandler(async () => {
      const result = await onAddAccount(getAccountData());
      if (result.success) {
        form.setValue(thirdPartyBillingDefault());
        onClose();
      }
    });

    const handleUseOnce = useHandler(async () => {
      const result = await onAddAccount(getAccountData(), { hidden: true });
      if (result.success) {
        form.setValue(thirdPartyBillingDefault());
        onClose();
      }
    });

    const canSubmit = form.allErrors.length === 0;

    return (
      <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
        <RedoModalHeader
          cancelClicked={onClose}
          subtitle="Add third-party accounts to bill for specific shipments. Please ensure the account information is correct or you will not be able to buy labels."
          title="Add third party billing account"
        />

        <Flex dir="column" gap="xl" p="xl">
          <CreateThirdPartyBillingForm form={form} />
        </Flex>

        {showUseOnceOption ? (
          <RedoModalFooter
            primaryButton={{
              disabled: !canSubmit,
              onClick: handleSubmit,
              text: "Save & use",
              size: "sm",
            }}
            secondaryButton={{
              disabled: !canSubmit,
              onClick: handleUseOnce,
              text: "Use once",
              size: "sm",
            }}
          />
        ) : (
          <RedoModalFooter
            primaryButton={{
              disabled: !canSubmit,
              onClick: handleSubmit,
              text: "Submit",
            }}
            secondaryButton={{ onClick: onClose, text: "Cancel" }}
          />
        )}
      </RedoBaseModal>
    );
  },
);
